import { useCallback, useRef, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { 
  setProjects, 
  addProject, 
  setLoadingProjects, 
  setProjectsLoadAttempted,
  setProjectsError, 
  setActiveProject, 
  loadMoreProjects,
  renameProject,
  deleteProject
} from 'features/conversation-slice';
import { useAiChatApiService } from 'api/providers/AppServiceProvider';
import { Project } from 'api/services/ai-chat-api-service';

export const useProjects = () => {
  const dispatch = useAppDispatch();
  const aiChatApiService = useAiChatApiService();
  const aiChatApiServiceRef = useRef(aiChatApiService);
  const loadAttemptedRef = useRef(false);
  
  // Update ref when service changes
  aiChatApiServiceRef.current = aiChatApiService;
  
  const { 
    projects, 
    visibleProjects, 
    loadingProjects, 
    projectsError, 
    activeProject,
    projectsLoaded
  } = useAppSelector((state) => state.conversations);

  // Create a new project
  const createProject = useCallback(async (projectName: string) => {
    try {
      dispatch(setLoadingProjects(true));
      dispatch(setProjectsError(null));
      
      const newProject = await aiChatApiServiceRef.current.createProject(projectName);
      dispatch(addProject(newProject));
      
      return newProject;
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to create project';
      dispatch(setProjectsError(errorMessage));
      throw error;
    } finally {
      dispatch(setLoadingProjects(false));
    }
  }, [dispatch]);

  // Load all projects
  const loadProjects = useCallback(async () => {
    // Prevent multiple simultaneous calls or if already attempted
    if (loadingProjects || projectsLoaded || loadAttemptedRef.current) {
      return;
    }
    
    // Mark as attempted immediately to prevent race conditions
    loadAttemptedRef.current = true;
    
    try {
      dispatch(setLoadingProjects(true));
      dispatch(setProjectsError(null));
      
      const response = await aiChatApiServiceRef.current.getProjectList();
      dispatch(setProjects(response.projects));
      
      return response.projects;
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load projects';
      dispatch(setProjectsError(errorMessage));
      // Mark as attempted even if failed to prevent retries
      dispatch(setProjectsLoadAttempted());
      throw error;
    } finally {
      dispatch(setLoadingProjects(false));
    }
  }, [dispatch, loadingProjects, projectsLoaded]);

  // Load more projects (show next 2)
  const loadMore = useCallback(() => {
    dispatch(loadMoreProjects());
  }, [dispatch]);

  // Set active project
  const selectProject = useCallback((project: Project | null) => {
    dispatch(setActiveProject(project));
  }, [dispatch]);

  // Rename a project
  const renameProjectAction = useCallback(async (projectId: string, newName: string) => {
    try {
      dispatch(setLoadingProjects(true));
      dispatch(setProjectsError(null));
      
      await aiChatApiServiceRef.current.renameProject(projectId, newName);
      dispatch(renameProject({ projectId, newName }));
      
      return true;
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to rename project';
      dispatch(setProjectsError(errorMessage));
      throw error;
    } finally {
      dispatch(setLoadingProjects(false));
    }
  }, [dispatch]);

  // Delete a project
  const deleteProjectAction = useCallback(async (projectId: string) => {
    try {
      dispatch(setLoadingProjects(true));
      dispatch(setProjectsError(null));
      
      await aiChatApiServiceRef.current.deleteProject(projectId);
      dispatch(deleteProject(projectId));
      
      return true;
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to delete project';
      dispatch(setProjectsError(errorMessage));
      throw error;
    } finally {
      dispatch(setLoadingProjects(false));
    }
  }, [dispatch]);

  // Check if there are more projects to load
  const hasMoreProjects = useMemo(() => {
    return visibleProjects.length < projects.length;
  }, [visibleProjects.length, projects.length]);

  return {
    // State
    projects,
    visibleProjects,
    loadingProjects,
    projectsError,
    activeProject,
    hasMoreProjects,
    projectsLoaded,
    
    // Actions
    createProject,
    loadProjects,
    loadMore,
    selectProject,
    renameProject: renameProjectAction,
    deleteProject: deleteProjectAction
  };
};
